import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Server,
  Activity,
  AlertTriangle,
  CheckCircle,
  Cpu,
  HardDrive,
  MemoryStick,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { computeEffectiveStatus } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SecurityScoreBadge } from "@/components/SecurityScoreBadge";

interface ServerStats {
  total: number;
  online: number;
  offline: number;
  warning: number;
  critical: number;
}

interface StatusIssue {
  metric: string;
  value: number;
  threshold: number;
  severity: "warning" | "critical";
  icon: React.ReactNode;
  action: string;
}

function getStatusIssues(server: any): StatusIssue[] {
  const issues: StatusIssue[] = [];

  // CPU checks
  if (server.cpu_usage !== null && server.cpu_usage !== undefined) {
    if (server.cpu_usage > 90) {
      issues.push({
        metric: "CPU Usage",
        value: server.cpu_usage,
        threshold: 90,
        severity: "critical",
        icon: <Cpu className="h-3 w-3" />,
        action: "Check for runaway processes or consider scaling resources",
      });
    } else if (server.cpu_usage > 75) {
      issues.push({
        metric: "CPU Usage",
        value: server.cpu_usage,
        threshold: 75,
        severity: "warning",
        icon: <Cpu className="h-3 w-3" />,
        action: "Monitor CPU usage and identify resource-intensive processes",
      });
    }
  }

  // Memory checks
  if (server.memory_usage !== null && server.memory_usage !== undefined) {
    if (server.memory_usage > 90) {
      issues.push({
        metric: "Memory Usage",
        value: server.memory_usage,
        threshold: 90,
        severity: "critical",
        icon: <MemoryStick className="h-3 w-3" />,
        action: "Free up memory or add more RAM to the server",
      });
    } else if (server.memory_usage > 75) {
      issues.push({
        metric: "Memory Usage",
        value: server.memory_usage,
        threshold: 75,
        severity: "warning",
        icon: <MemoryStick className="h-3 w-3" />,
        action: "Review memory-heavy applications and consider optimization",
      });
    }
  }

  // Disk checks
  if (server.disk_usage !== null && server.disk_usage !== undefined) {
    if (server.disk_usage > 85) {
      issues.push({
        metric: "Disk Usage",
        value: server.disk_usage,
        threshold: 85,
        severity: "critical",
        icon: <HardDrive className="h-3 w-3" />,
        action: "Clean up disk space or expand storage capacity",
      });
    } else if (server.disk_usage > 75) {
      issues.push({
        metric: "Disk Usage",
        value: server.disk_usage,
        threshold: 75,
        severity: "warning",
        icon: <HardDrive className="h-3 w-3" />,
        action: "Review disk usage and remove unnecessary files",
      });
    }
  }

  return issues;
}

export default function DashboardPage() {
  const { profile } = useAuth();
  const [servers, setServers] = useState<any[]>([]);
  const [stats, setStats] = useState<ServerStats>({
    total: 0,
    online: 0,
    offline: 0,
    warning: 0,
    critical: 0,
  });
  const [loading, setLoading] = useState(true);
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (profile?.tenant_id) {
      fetchServers();

      // Real-time subscription for live updates
      const channel = supabase
        .channel('dashboard-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'servers', filter: `tenant_id=eq.${profile.tenant_id}` }, () => {
          fetchServers();
        })
        .subscribe();

      return () => { supabase.removeChannel(channel); };
    }
  }, [profile]);

  const fetchServers = async () => {
    try {
      const { data: serversData, error } = await supabase
        .from("v_server_status")
        .select("*")
        .eq("tenant_id", profile.tenant_id);

      if (error) throw error;

      // Fetch security-relevant metrics for accurate security scores
      const hostnames = serversData?.map((s: any) => s.hostname) || [];
      let latestMetrics: Record<string, any> = {};

      if (hostnames.length > 0) {
        const metricsPromises = hostnames.map(async (hostname: string) => {
          const { data } = await supabase
            .from('metrics_history')
            .select('hostname, secure_boot_enabled, tpm_enabled, firewall_enabled, windows_defender_enabled, windows_defender_updated, failed_login_last_24h, admin_accounts_count, bitlocker_status, smbv1_enabled, pending_updates_count, pending_security_updates, antivirus_product, antivirus_updated')
            .eq('tenant_id', profile.tenant_id)
            .eq('hostname', hostname)
            .order('timestamp', { ascending: false })
            .limit(1)
            .maybeSingle();
          return data;
        });

        const metricsResults = await Promise.all(metricsPromises);
        for (const m of metricsResults) {
          if (m?.hostname) latestMetrics[m.hostname] = m;
        }
      }

      const mergedServers = (serversData || []).map((server: any) => ({
        ...server,
        ...latestMetrics[server.hostname],
        id: server.id,
        status: server.status,
        online: server.online,
      }));

      setServers(mergedServers);

      // Calculate stats using online boolean from view
      const total = serversData?.length || 0;
      const online = serversData?.filter((s: any) => s.online).length || 0;
      const offline = total - online;
      const warning = serversData?.filter((s: any) => s.status === "warning").length || 0;
      const critical = serversData?.filter((s: any) => s.status === "critical").length || 0;

      setStats({ total, online, offline, warning, critical });
    } catch (error) {
      console.error("Error fetching servers:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      online: { variant: "default" as const, className: "bg-success text-success-foreground" },
      offline: { variant: "secondary" as const, className: "" },
      warning: { variant: "default" as const, className: "bg-warning text-warning-foreground" },
      critical: { variant: "destructive" as const, className: "" },
    };
    return variants[status as keyof typeof variants] || variants.offline;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "online":
        return <CheckCircle className="h-4 w-4" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4" />;
      case "critical":
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Server className="h-4 w-4" />;
    }
  };

  const toggleExpanded = (serverId: string) => {
    setExpandedServers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(serverId)) {
        newSet.delete(serverId);
      } else {
        newSet.add(serverId);
      }
      return newSet;
    });
  };

  const getEffectiveStatus = (server: any) => {
    return server.online ? "online" : ["warning", "critical"].includes(server.status) ? server.status : "offline";
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <div className="animate-pulse">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="h-4 bg-muted rounded w-20"></div>
                  <div className="h-4 w-4 bg-muted rounded"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-muted rounded w-16 mb-1"></div>
                  <div className="h-3 bg-muted rounded w-24"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
          Dashboard
        </h1>
        <p className="text-muted-foreground">Welcome back, {profile?.full_name}. Here is your server overview.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card
          className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300"
          style={{ boxShadow: "var(--shadow-3d)" }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium">Total Servers</CardTitle>
            <div className="p-2 bg-primary/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <Server className="h-4 w-4 text-primary" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Active monitoring targets</p>
          </CardContent>
        </Card>

        <Card
          className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300"
          style={{ boxShadow: "var(--shadow-3d)" }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-success/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium">Online</CardTitle>
            <div className="p-2 bg-success/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <CheckCircle className="h-4 w-4 text-success" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold text-success">{stats.online}</div>
            <p className="text-xs text-muted-foreground">Online servers</p>
          </CardContent>
        </Card>

        <Card
          className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300"
          style={{ boxShadow: "var(--shadow-3d)" }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-warning/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium">Warnings</CardTitle>
            <div className="p-2 bg-warning/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <AlertTriangle className="h-4 w-4 text-warning" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold text-warning">{stats.warning}</div>
            <p className="text-xs text-muted-foreground">Require attention</p>
          </CardContent>
        </Card>

        <Card
          className="relative overflow-hidden group hover:shadow-[var(--shadow-3d-hover)] transition-all duration-300"
          style={{ boxShadow: "var(--shadow-3d)" }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-critical/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
            <CardTitle className="text-sm font-medium">Critical</CardTitle>
            <div className="p-2 bg-critical/10 rounded-lg group-hover:scale-110 transition-transform duration-300">
              <AlertTriangle className="h-4 w-4 text-critical" />
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            <div className="text-2xl font-bold text-critical">{stats.critical}</div>
            <p className="text-xs text-muted-foreground">Immediate action required</p>
          </CardContent>
        </Card>
      </div>

      <Card className="relative overflow-hidden" style={{ boxShadow: "var(--shadow-3d)" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-50" />
        <CardHeader className="relative z-10">
          <CardTitle>Recent Servers</CardTitle>
          <CardDescription>Latest servers added to monitoring</CardDescription>
        </CardHeader>
        <CardContent className="relative z-10">
          {servers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Server className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No servers yet</p>
              <p>Start by installing monitoring agents on your servers.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {servers.slice(0, 5).map((server) => {
                const effectiveStatus = getEffectiveStatus(server);
                const issues = getStatusIssues(server);
                const hasIssues = issues.length > 0;
                const isExpanded = expandedServers.has(server.id);

                return (
                  <div key={server.id} className="border rounded-lg overflow-hidden">
                    <div
                      className="flex items-center justify-between p-4 hover:bg-accent/50 transition-all duration-200 group cursor-pointer"
                      onClick={() => toggleExpanded(server.id)}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-muted rounded-lg group-hover:bg-primary/10 transition-colors duration-200">
                          {getStatusIcon(effectiveStatus)}
                        </div>
                        <div>
                          <p className="font-medium">{server.hostname}</p>
                          <p className="text-sm text-muted-foreground">{server.ip_address}</p>
                        </div>
                        <SecurityScoreBadge server={server} compact />
                      </div>
                      <div className="flex items-center space-x-4">
                        <Badge {...getStatusBadge(effectiveStatus)}>{effectiveStatus}</Badge>
                        <div className="text-right text-sm">
                          <p>
                            {server.cpu_usage !== null && server.cpu_usage !== undefined
                              ? `CPU: ${server.cpu_usage.toFixed(1)}%`
                              : ""}
                          </p>
                          <p className="text-muted-foreground">
                            {server.last_seen ? new Date(server.last_seen).toLocaleString() : "Never"}
                          </p>
                        </div>
                        <div className="text-muted-foreground">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 pt-2 border-t bg-muted/30 space-y-2">
                        {hasIssues ? (
                          <>
                            <p className="text-sm font-medium text-muted-foreground mb-2">
                              Issues & Recommended Actions:
                            </p>
                            {issues.map((issue, idx) => (
                              <div
                                key={idx}
                                className={`p-3 rounded-lg border ${
                                  issue.severity === "critical"
                                    ? "bg-critical/10 border-critical/30"
                                    : "bg-warning/10 border-warning/30"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={issue.severity === "critical" ? "text-critical" : "text-warning"}>
                                    {issue.icon}
                                  </span>
                                  <span
                                    className={`font-medium text-sm ${issue.severity === "critical" ? "text-critical" : "text-warning"}`}
                                  >
                                    {issue.metric}: {issue.value.toFixed(1)}% (threshold: {issue.threshold}%)
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground ml-5">→ {issue.action}</p>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/30">
                            <CheckCircle className="h-4 w-4 text-success" />
                            <span className="text-sm font-medium text-success">This endpoint is healthy</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
